/**
 * Profit & Loss.
 *
 * The owner's first question — "did we make money?" — had no screen,
 * though the API and the PDF have supported it all along.
 *
 * Laid out as a statement rather than a table, which is both how an
 * accountant reads it and the only shape that survives a phone.
 *
 * Statement order is kept -- revenue, direct costs, gross, overheads,
 * net -- so the two profit lines land where an accountant expects them,
 * at the end of their own sections. They are given weight instead of
 * position: a ruled band for gross, an ink band for net, and a loss
 * coloured so it cannot be read as a profit at a glance.
 *
 * That does mean an owner on a phone scrolls to reach the net figure.
 * Answering before the scroll is a different screen -- a summary that
 * leads with the number and drills in here -- and it belongs there
 * rather than being bolted onto a statement.
 */
import { useQuery } from '@tanstack/react-query'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchProfitLoss } from '../api/endpoints'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { c, fmtMoney } from '../theme'

/* Figures on the navy result band. White for a profit; a light red for a
   loss, which still clears 3:1 at this size on both themes' navy. */
const LEAD_PROFIT = '#FFFFFF'
const LEAD_LOSS = '#FFC9C2'

interface Section {
  code: string
  name_en: string
  name_ar: string | null
  amount: string
  ledgers: { code: string; name_en: string; amount: string }[]
}

interface Statement {
  from_date: string
  to_date: string
  direct_income: Section[]
  direct_expenses: Section[]
  gross_profit: string
  indirect_income: Section[]
  indirect_expenses: Section[]
  net_profit: string
}

/** A profit line. The figure, not the label, is what the eye should hit. */
function ProfitLine({ label, value, lead }:
{ label: string; value: string; lead?: boolean }) {
  const negative = Number(value) < 0
  return (
    <Stack direction="row" alignItems="baseline" justifyContent="space-between"
      sx={{
        px: 2, py: lead ? 1.5 : 1.2,
        bgcolor: lead ? c.primary : c.surfacePage,
        color: lead ? c.onPrimary : undefined,
        borderTop: `1px solid ${lead ? c.primary : c.border}`,
      }}>
      <Typography sx={{
        fontSize: lead ? 13 : 12, fontWeight: 600,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        color: lead ? 'rgba(255,255,255,0.82)' : c.inkMuted,
      }}>
        {label}
      </Typography>
      <span className={`num ${lead ? 'num-lg' : ''}`}
        style={{
          fontWeight: 600,
          // A loss is the one number on this page that must not be
          // mistaken for a profit at a glance.
          color: lead ? (negative ? LEAD_LOSS : LEAD_PROFIT)
                      : (negative ? c.dangerInk : c.ink),
        }}>
        {fmtMoney(value)}
      </span>
    </Stack>
  )
}

export default function ProfitLoss({ onLedger }:
{ onLedger?: (code: string) => void }) {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()

  const q = useQuery({
    queryKey: ['profit-loss', range.from, range.to],
    queryFn: () => fetchProfitLoss({
      from_date: range.from, to_date: range.to }) as Promise<Statement>,
  })
  const pl = q.data

  /** One heading and its accounts. Stacks, so it works at any width. */
  const block = (label: string, sections: Section[]) => {
    if (!sections.length) return null
    return (
      <Box>
        <Typography className="section-label"
          sx={{ px: 2, pt: 2, pb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Box className="ruled">
          {sections.map((s) => (
            <Box key={s.code} sx={{ px: 2, py: 1 }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                  {s.name_en}
                </Typography>
                <span className="num" style={{ fontWeight: 500 }}>
                  {fmtMoney(s.amount)}
                </span>
              </Stack>
              {s.ledgers.map((l) => (
                <Stack key={l.code} direction="row" spacing={2}
                  justifyContent="space-between"
                  onClick={() => onLedger?.(l.code)}
                  sx={{
                    pl: 2, py: 0.4,
                    cursor: onLedger ? 'pointer' : 'default',
                    '&:hover': { bgcolor: onLedger ? c.primarySoft : undefined },
                  }}>
                  <Typography sx={{ fontSize: 12.5, color: c.inkMuted,
                                    minWidth: 0 }}>
                    {l.code} · {l.name_en}
                  </Typography>
                  <span className="num"
                    style={{ fontSize: 12.5, color: c.inkMuted,
                             whiteSpace: 'nowrap' }}>
                    {fmtMoney(l.amount)}
                  </span>
                </Stack>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  const empty = pl && !pl.direct_income.length && !pl.direct_expenses.length
    && !pl.indirect_income.length && !pl.indirect_expenses.length

  return (
    <ReportShell titleKey="nav.profitLoss"
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          pdf={{ path: 'profit-loss.pdf',
                 params: { from_date: range.from, to_date: range.to } }}
          onExport={pl ? () => downloadCsv(`profit-loss-${range.to}`, [
            ['Section', 'Group', 'Code', 'Account', 'Amount'],
            ...([
              ['Direct income', pl.direct_income],
              ['Direct expenses', pl.direct_expenses],
              ['Indirect income', pl.indirect_income],
              ['Indirect expenses', pl.indirect_expenses],
            ] as [string, Section[]][]).flatMap(([name, secs]) =>
              secs.flatMap((s) => [
                [name, s.name_en, '', '', s.amount],
                ...s.ledgers.map((l) => [name, s.name_en, l.code,
                                         l.name_en, l.amount]),
              ])),
            ['', 'Gross profit', '', '', pl.gross_profit],
            ['', 'Net profit', '', '', pl.net_profit],
          ]) : undefined} />
      }>
      {q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
      ) : empty ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          {t('report.nothingInRange')}
        </Typography>
      ) : pl ? (
        <Box>
          {block(t('pnl.directIncome'), pl.direct_income)}
          {block(t('pnl.directExpenses'), pl.direct_expenses)}
          <ProfitLine label={t('pnl.grossProfit')} value={pl.gross_profit} />

          {block(t('pnl.indirectIncome'), pl.indirect_income)}
          {block(t('pnl.indirectExpenses'), pl.indirect_expenses)}
          <ProfitLine label={t('pnl.netProfit')} value={pl.net_profit} lead />
        </Box>
      ) : null}
      <ReportFooter />
    </ReportShell>
  )
}
